import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatButtonToggle } from '@angular/material/button-toggle';
import { AbcCore, CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import {
  ICoreDataEntity,
  ObjectTypeInfoColumnViewModel,
  ObjectTypeInfoViewModel,
  ValidateModel,
} from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { AbcComponentService, AbcListComponent } from '@barthauer/abc/ui-components';
import { ItemClickEvent } from 'devextreme/ui/list';
import { Subscription } from 'rxjs';
import { AbcArenaService } from '../../abc-arena.service';
import { DataService } from '../../services/data.service';
import { GoToService } from '../../services/go-to.service';
import { ArenaService } from '../arena.service';
import { DynamicTabsDirective } from '../data-form/core/dynamic.directive';
import { DataFormSettings } from '../data-form/core/settings.model';
import { GoToComponent } from '../go-to/go-to.component';
import { ActionsAndInfoDetails } from '../models/data.model';
import { NavigationLayoutViewModel } from '../navigation/navigation.viewModel';
import { LayoutViewModel, SubLayoutReferenceViewModel, SubLayoutViewModel } from '../viewModels/layout.viewModel';
import {
  AdditionalHigonAction,
  HigonAction,
  HigonItemType,
  LayoutReferenceInfo,
  LayoutReferencesInfo,
  NavigationAndActionDataSource,
  NavigationAndActionDataSourceItem,
  TargetLayoutInfo,
} from './higon.model';
import { HigonService } from './higon.service';

export class RefColumnInfo {
  objectType: ObjectTypeInfoViewModel;
  column: string;
  denyInsert: boolean;
}

@Component({
  selector: 'arena-higon',
  templateUrl: './higon.component.html',
  styleUrls: ['./higon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HigonComponent implements OnInit, OnDestroy {
  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;

  @ViewChild('documentsButton', { static: true }) documentsButton: MatButtonToggle;
  @ViewChild('commentsButton', { static: true }) commentsButton: MatButtonToggle;
  @ViewChild('propertiesButton', { static: true }) propertiesButton: MatButtonToggle;
  @ViewChild('higonListBox', { static: true }) higonListBox: AbcListComponent;
  @ViewChild(DynamicTabsDirective, { static: true }) protected tabsDirective!: DynamicTabsDirective;
  private _arenaGoTo: GoToComponent;
  private get arenaGoTo(): GoToComponent {
    if (!this._arenaGoTo) {
      const cpt = this.arenaService.loadComponent(GoToComponent, this.tabsDirective);
      this._arenaGoTo = <GoToComponent>cpt.componentRef.instance;
      document.getElementById('dataFormComponent').appendChild(cpt.domElement);
    }
    return this._arenaGoTo;
  }

  private layoutViewModel: LayoutViewModel;
  public get higonTabs(): SubLayoutViewModel[] {
    return this.layoutViewModel?.subLayoutViews ?? [];
  }

  private subscriptions: Subscription[] = [];

  public actionAndInfoDetails: ActionsAndInfoDetails = new ActionsAndInfoDetails();
  public listBoxHeight: number = window.innerHeight - 150;
  public navigationAndActionDataSource: NavigationAndActionDataSource[] = [];
  public noDataStyle: boolean = false;
  public showTemplateImage: boolean = false;

  public getDataFormSettings: () => DataFormSettings = () => undefined;
  public get settings(): DataFormSettings {
    return this.getDataFormSettings();
  }
  public togglePDC: (identifier: string) => Promise<void> = (identifier) => Promise.resolve();
  public openSubLayout: (target: TargetLayoutInfo) => Promise<void> = (target) => Promise.resolve();

  @Input() rtlEnabled: boolean;

  public abcListDisplayTextDictionary: {} = [];
  public listBoxDisabled: boolean;

  constructor(
    public arenaService: ArenaService,
    private higonService: HigonService,
    private dataService: DataService,
    private abcCore: AbcCore,
    private goTo: GoToService,
    private abcArenaService: AbcArenaService,
    private abcComponentService: AbcComponentService,
    private changeDetector: ChangeDetectorRef,
    resourceManagerService: ResourceManagerService,
  ) {
    this.resAsync = resourceManagerService.getResourceSet('abc-arena-higon');
  }

  public async ngOnInit() {
    this.res = await this.resAsync;
    this.subscriptions.push(
      this.arenaService.updateActionAndInfoDetails$.subscribe((data: { isIncremented; elementName }) => {
        Object.keys(this.actionAndInfoDetails)?.map((key) => {
          if (this.actionAndInfoDetails[key] === '') {
            this.actionAndInfoDetails[key] = 0;
          }

          if (key === data.elementName) {
            if (data.isIncremented === true) {
              this.actionAndInfoDetails[key] = this.actionAndInfoDetails[key] + 1;
            }

            if (data.isIncremented === false) {
              this.actionAndInfoDetails[key] = this.actionAndInfoDetails[key] - 1;
            }
          }

          // if (this.actionAndInfoDetails[key] === 0) {
          //   this.actionAndInfoDetails[key] = '';
          // }
        });
      }),
    );

    this.abcListDisplayTextDictionary[HigonItemType.Navigation] = this.arenaService.res('Higon-Navigation');
    this.abcListDisplayTextDictionary[HigonItemType.Commands] = this.arenaService.res('Higon-Tasks');
  }

  private async tryGetReferencingObjectTypeAndColumn(
    itemData: NavigationAndActionDataSourceItem,
  ): Promise<RefColumnInfo> {
    try {
      const validate = await this.validateOPath(itemData.opath);
      const refObjectType = await this.abcCore.objectSpace.getObjectType(validate.RefObjectTypeName).toPromise();
      const opathSegments = validate?.Segments?.filter((_) => _.Type !== 'Cast' && _.Type !== 'Match');
      let referencingColumn: ObjectTypeInfoColumnViewModel;
      if (opathSegments?.length === 1 && opathSegments[0]?.ReferencingColumnName) {
        const firstRefObjectType = await this.abcCore.objectSpace
          .getObjectType(opathSegments[0].RefObjectTypeName)
          .toPromise();
        const referencingColumnName = opathSegments[0]?.ReferencingColumnName;
        if (referencingColumnName) {
          referencingColumn = firstRefObjectType?.columnsByName.get(referencingColumnName);
        }
      }

      return {
        objectType: refObjectType,
        column: referencingColumn?.name,
        denyInsert: this.shouldDenyInsertCore(validate),
      };
    } catch (err) {
      this.abcCore.error.publish(err);
    }
  }

  private async tryFindTargetLayoutsFromObjectType(
    referencingObject: RefColumnInfo,
  ): Promise<SubLayoutReferenceViewModel[]> {
    try {
      const matchingLayouts: NavigationLayoutViewModel[] = await this.goTo.lookForPossibleTargets(
        referencingObject.objectType?.name,
        this.abcArenaService.currentDataEntity.Id,
        this.abcArenaService.currentDataEntity.ObjectType,
      );

      const matchedLayouts: SubLayoutReferenceViewModel[] = [];
      if (matchingLayouts?.length) {
        for (const layout of matchingLayouts) {
          this.appendSubLayoutReferencesCore(
            matchedLayouts,
            this.layoutViewModel.subLayoutReferences.filter((slr) => slr.targetLayout === layout.layoutName),
          );
        }
      }

      if (!matchedLayouts?.length) {
        //TODO: Create SubLayoutReference objects dynamically
      }

      return matchedLayouts;
    } catch (err) {
      this.abcCore.error.publish(err);
    }
  }

  private findDistinctSubLayoutReferences(layoutReferencesInfo: LayoutReferencesInfo): SubLayoutReferenceViewModel[] {
    const defaultLayoutReferenceId = layoutReferencesInfo?.defaultLayoutReferenceId;
    const layoutReferences: LayoutReferenceInfo[] = layoutReferencesInfo?.layoutReferences;
    const subLayoutReferences: SubLayoutReferenceViewModel[] = [];

    if (layoutReferences?.length) {
      for (const layoutReference of layoutReferences) {
        this.appendSubLayoutReferences(subLayoutReferences, layoutReference.layoutReferenceId);
      }
    }

    if (!subLayoutReferences.length && defaultLayoutReferenceId) {
      this.appendSubLayoutReferences(subLayoutReferences, defaultLayoutReferenceId);
    }
    return subLayoutReferences;
  }

  private appendSubLayoutReferences(
    subLayoutReferences: SubLayoutReferenceViewModel[],
    targetLayoutReferenceId: string,
  ): void {
    const found = this.findSubLayoutReferences(targetLayoutReferenceId);
    this.appendSubLayoutReferencesCore(subLayoutReferences, found);
  }

  private appendSubLayoutReferencesCore(
    subLayoutReferences: SubLayoutReferenceViewModel[],
    foundSubLayoutReferences: SubLayoutReferenceViewModel[],
  ): void {
    if (foundSubLayoutReferences) {
      for (const subLayoutReference of foundSubLayoutReferences) {
        if (!subLayoutReferences.find((t) => t.targetLayout === subLayoutReference.targetLayout)) {
          subLayoutReferences.push(subLayoutReference);
        }
      }
    }
  }

  private findSubLayoutReferences(layoutReferenceId: string): SubLayoutReferenceViewModel[] {
    return this.layoutViewModel.subLayoutReferences.filter((_) => _.id.startsWith(layoutReferenceId));
  }

  private async gotoTargetLayout(
    subLayoutReferences: SubLayoutReferenceViewModel[],
    referencingObject: RefColumnInfo,
    openInNewTab: boolean,
  ): Promise<void> {
    if (subLayoutReferences?.length) {
      const targets: { layout: NavigationLayoutViewModel; target: TargetLayoutInfo }[] = [];
      for (const tl of subLayoutReferences) {
        const nly = this.arenaService.navigationViewModelData.findBestLayoutByLayoutId(tl.targetLayout);
        if (nly) {
          const target: TargetLayoutInfo = {
            layout: nly,
            sourceId: undefined,
            sourceObjectType: undefined,
            targetLayoutOPath: tl.targetLayoutOPath,
            column: referencingObject?.column,
            isColumnInTarget: true,
            openInNewTab: openInNewTab,
            denyInsert:
              referencingObject?.denyInsert ||
              (!referencingObject && (await this.shouldDenyInsert(tl.targetLayoutOPath))),
          };
          targets.push({ layout: nly, target: target });
        }
      }
      await this.arenaGoTo.showTargetNavigationLayouts(targets);
    }
  }

  private async validateOPath(opath: string): Promise<ValidateModel> {
    const currentObjectType = await this.abcCore.objectSpace.getObjectType(this.layoutViewModel.objectType).toPromise();

    return ConvertHelper.arrayBufferToObject(
      await this.dataService.validateOpath(currentObjectType.name, opath).toPromise(),
    );
  }

  private async shouldDenyInsert(targetLayoutOPath: string): Promise<boolean> {
    const validate = await this.validateOPath(targetLayoutOPath);
    return this.shouldDenyInsertCore(validate);
  }

  private shouldDenyInsertCore(validate: ValidateModel): boolean {
    const opathSegments = validate?.Segments?.filter((_) => _.Type !== 'Cast' && _.Type !== 'Match');
    return (opathSegments?.length && opathSegments[0].Type === 'Type') || opathSegments?.length > 1;
  }

  public async loadLayout(
    layoutViewModel: LayoutViewModel,
    filter: string,
    objectIds: string[],
    parameterValues: ParameterValue[],
  ): Promise<void> {
    this.navigationAndActionDataSource = [];
    this.noDataStyle = false;
    this.layoutViewModel = layoutViewModel;

    if (!layoutViewModel) {
      return;
    }

    const settings = this.settings;
    if (settings) {
      const ot = await this.abcCore.objectSpace.getObjectType(layoutViewModel.objectType).toPromise();
      setTimeout(() => {
        settings.isCommentsAvailable = ot?.referencingByName?.has('Comments');
        settings.isDocumentsAvailable = ot?.referencingByName?.has('Documents');
        settings.isPropertiesAvailable = ot?.referencingByName?.has('Properties');
      }, 100);
    }
    if (this.higonTabs?.length) {
      const navigation: NavigationAndActionDataSource = {
        key: HigonItemType.Navigation,
        displayText: this.arenaService.res('Higon-Navigation'),
        items: [],
      };
      this.navigationAndActionDataSource.push(navigation);
      for (let i = 0; i < this.higonTabs.length; i++) {
        const higonTab = this.higonTabs[i];
        navigation.items.push(
          new NavigationAndActionDataSourceItem({
            id: 'T' + i,
            opath: higonTab.opath,
            text: higonTab.caption,
            defaultLayoutReferenceIdExpression: higonTab.defaultLayoutReferenceId,
            layoutReferenceIdExpression: higonTab.layoutReferenceId,
            type: HigonItemType.Navigation,
          }),
        );
      }
    }

    this.showTemplateImage = false;
    const actions = await this.abcArenaService.actions.findAdditionalHigonActions(layoutViewModel.objectType);
    if (actions?.length) {
      const tasks: NavigationAndActionDataSource = {
        key: HigonItemType.Commands,
        displayText: this.arenaService.res('Higon-Tasks'),
        items: actions,
      };
      this.navigationAndActionDataSource.push(tasks);
      if (actions.some((_) => _.imageSrc || _.imageType)) {
        this.showTemplateImage = true;
        this.changeDetector.detectChanges();
      }
      if (actions.some((_) => _.listBoxEnabled)) {
        this.listBoxDisabled = !actions[0].listBoxEnabled;
      }
    }
  }

  public async setCurrentObject(object: ICoreDataEntity): Promise<void> {
    if (!this.layoutViewModel) {
      return;
    }

    const parameters = [new ParameterValue('Id', object?.Id)];
    const query = this.higonService.createHigonQuery(this.layoutViewModel, this.higonTabs, parameters);

    for (const higon of this.navigationAndActionDataSource) {
      for (const item of higon.items) {
        if (item instanceof NavigationAndActionDataSourceItem && !CompareHelper.isEmptyObject(object)) {
          item.badge = 0;
        }
      }
    }

    let dataObject: ICoreDataEntity;
    if (object?.Id) {
      dataObject = ConvertHelper.arrayBufferToObject(await this.dataService.readObjects(query).toPromise());

      for (const key in dataObject) {
        if (dataObject.hasOwnProperty(key)) {
          const navigation = this.navigationAndActionDataSource.find((n) =>
            n.items.find((i) => i.type === HigonItemType.Navigation),
          );
          const item = navigation?.items.find(
            (element) => element.type === HigonItemType.Navigation && element.id === key,
          );

          if (item instanceof NavigationAndActionDataSourceItem) {
            if (dataObject[key] !== 0) {
              item.badge = dataObject[key];
              // item.disabled = false;
            } else {
              item.badge = 0;
              // item.disabled = true;
            }
          }
        }
      }
      try {
        this.higonListBox.instance?.reload();
      } catch (err) {
        console.error(err);
      }
    } else {
      const navigation = this.navigationAndActionDataSource.find((n) =>
        n.items.find((i) => i.type === HigonItemType.Navigation),
      );
      if (navigation?.items) {
        for (const item of navigation?.items) {
          if (item instanceof NavigationAndActionDataSourceItem) {
            item.badge = 0;
          }
        }
      }
      for (const key in this.actionAndInfoDetails) {
        if (Object.prototype.hasOwnProperty.call(this.actionAndInfoDetails, key)) {
          this.actionAndInfoDetails[key] = '';
        }
      }
    }

    this.actionAndInfoDetails = <ActionsAndInfoDetails>(<unknown>dataObject) ?? new ActionsAndInfoDetails();

    if (!CompareHelper.isEmptyObject(this.actionAndInfoDetails)) {
      Object.keys(this.actionAndInfoDetails)?.map((key, index) => {
        if (this.actionAndInfoDetails[key] === 0) this.actionAndInfoDetails[key] = '';
      });
    }

    this.changeDetector.markForCheck();
  }

  private isBusy: boolean = false;
  public async onItemClick(event: ItemClickEvent): Promise<void> {
    if (!this.isBusy) {
      try {
        this.isBusy = true;
        const item: NavigationAndActionDataSourceItem | AdditionalHigonAction = event?.itemData;
        if (item?.type === HigonItemType.Navigation) {
          if (this.abcArenaService.currentDataEntity?.Id) {
            const currentId: string = this.abcArenaService.currentDataEntity.Id;

            let defaultLayoutReferenceId: string = item.defaultLayoutReferenceIdExpression;
            const layoutReferenceId: string = item.layoutReferenceIdExpression;

            const refColumnInfo = await this.tryGetReferencingObjectTypeAndColumn(item);

            const query = this.higonService.createSpecialTypeQuery(
              this.layoutViewModel,
              currentId,
              item.opath,
              defaultLayoutReferenceId,
              layoutReferenceId,
            );

            const layoutReferencesInfo: LayoutReferencesInfo = ConvertHelper.arrayBufferToObject(
              await this.dataService.readObjects(query).toPromise(),
            );

            let subLayoutReferences = this.findDistinctSubLayoutReferences(layoutReferencesInfo);
            if (!subLayoutReferences?.length) {
              subLayoutReferences = await this.tryFindTargetLayoutsFromObjectType(refColumnInfo);
            }

            await this.gotoTargetLayout(subLayoutReferences, refColumnInfo, event?.event?.ctrlKey);
          }
        } else {
          const higonAction: HigonAction = {
            dataEntity: this.abcArenaService.currentDataEntity,
            arenaLayout: this.abcArenaService.actions?.arenaLayout,
          };
          await item.action(higonAction);
        }
      } finally {
        this.isBusy = false;
      }
    }
  }

  public openGalleryItem(option: string, toggledGroup: string[]) {
    this.togglePDC(option);
  }

  public getDateTime(date: string, time: string): string {
    return `${ConvertHelper.toLocaleFormattedDateString(date)} ${ConvertHelper.toTime(time)}`;
  }

  public ngOnDestroy(): void {
    this.navigationAndActionDataSource = [];
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
